/* eslint-disable import/no-internal-modules */
import PublishedInFilterSelector from '@insided/deprecated-control/overview/filters/PublishedInFilterSelector'
import {
  getBooleanSelectorItems,
  getDateSelectorItems,
  getRangeSelectorItems,
  normalizeCategoriesAndGroups
} from '@insided/deprecated-control/overview/filters/utils'
import {
  Group,
  ParentCategory
} from '@insided/deprecated-preact-components/category-selector/CategorySelector'
import SimpleFilterSelector from '@insided/deprecated-control/overview/filters/SimpleFilterSelector'
import { ContentType, IdeationStatus, ModerationLabel, ModeratorTag } from '../types'
import DynamicFilterSelector from '@insided/deprecated-control/overview/filters/DynamicFilterSelector'
import DateFilterSelector from '@insided/deprecated-control/overview/filters/DateFilterSelector'
import RadioInputFilterSelector from '@insided/deprecated-control/overview/filters/RadioInputFilterSelector'
import RadioFilterSelector from '@insided/deprecated-control/overview/filters/RadioFilterSelector'
import { capitalize } from '@insided/deprecated-preact-components/utils/string'
import {
  STATUS_DRAFT,
  STATUS_PUBLISHED,
  STATUS_SCHEDULED
} from '@insided/shared/utils/contentType'
import { FilterSelector } from '@insided/deprecated-control/overview/filters/FiltersMenu'

const createPublishedInFilter = (categories: Array<ParentCategory | Group>): FilterSelector =>
  PublishedInFilterSelector.create({
    id: 'category',
    name: 'Published in',
    items: normalizeCategoriesAndGroups(categories)
  })

const createContentTypeFilter = (contentTypes: ContentType[]): FilterSelector =>
  SimpleFilterSelector.create({
    id: 'contentType',
    name: 'Content type',
    items: contentTypes.map((contentType, idx) => ({
      id: idx,
      name: `${contentType.charAt(0).toUpperCase()}${contentType
        .replace(/([A-Z])/g, ' $1')
        .slice(1)}`,
      value: contentType
    }))
  })

const createAssignedModeratorFilter = (): FilterSelector =>
  DynamicFilterSelector.create({
    id: 'assignedModerator',
    name: 'Assigned moderator',
    items: [{ id: '', name: 'Unassigned', icon: 'user', value: '' }],
    fetchUrl: '/member/getModerators?query='
  })

const createPublicTagFilter = (): FilterSelector =>
  DynamicFilterSelector.create({
    id: 'publicTag',
    name: 'Public tag',
    items: [],
    isSearchable: true,
    fetchUrl: '/publicTag/list?query='
  })

const createAuthorFilter = (): FilterSelector =>
  DynamicFilterSelector.create({
    id: 'author',
    name: 'Author',
    items: [],
    fetchUrl: '/member/getAuthors?filterByRole=1&query='
  })

const createModerationLabelFilter = (moderationLabels: ModerationLabel[]): FilterSelector =>
  SimpleFilterSelector.create({
    id: 'moderationLabel',
    name: 'Moderation label',
    items: [
      ...[{ id: '', name: '(No moderation label)', value: '' }],
      ...moderationLabels.map((moderationLabel: ModerationLabel) => ({
        id: moderationLabel.id,
        name: moderationLabel.title,
        value: moderationLabel.title
      }))
    ],
    isSearchable: true
  })

const createModeratorTagFilter = (moderatorTags: ModeratorTag[]): FilterSelector =>
  SimpleFilterSelector.create({
    id: 'moderatorTag',
    name: 'Moderation tag',
    items: moderatorTags.map((moderatorTag: ModeratorTag) => ({
      id: moderatorTag.id,
      name: moderatorTag.text,
      value: moderatorTag.text
    })),
    isSearchable: true
  })

const createPublishedAtFilter = (): FilterSelector =>
  DateFilterSelector.create({
    id: 'publishedAt',
    name: 'Publish date',
    items: getDateSelectorItems()
  })

const createLastActivityAtFilter = (): FilterSelector =>
  DateFilterSelector.create({
    id: 'lastActivityAt',
    name: 'Last activity date',
    items: getDateSelectorItems()
  })

const createChangedIdeationStatusAtFilter = (): FilterSelector =>
  DateFilterSelector.create({
    id: 'changedIdeationStatusAt',
    name: 'Last ideation status change',
    items: getDateSelectorItems()
  })

const createIdeationStatusFilter = (
  ideationStatuses: IdeationStatus[],
  isMain: boolean = false
): FilterSelector =>
  SimpleFilterSelector.create({
    id: 'ideationStatus',
    name: 'Ideation status',
    items: ideationStatuses.map((ideationStatus: IdeationStatus) => ({
      id: ideationStatus.id,
      name: ideationStatus.name,
      value: ideationStatus.name
    })),
    isSearchable: true,
    isMain
  })

const createRepliesFilter = (): FilterSelector =>
  RadioInputFilterSelector.create({
    id: 'replies',
    name: 'Replies',
    items: getRangeSelectorItems()
  })

const createLikesFilter = (): FilterSelector =>
  RadioInputFilterSelector.create({
    id: 'likes',
    name: 'Likes',
    items: getRangeSelectorItems()
  })

const createVotesFilter = (): FilterSelector =>
  RadioInputFilterSelector.create({
    id: 'votes',
    name: 'Votes',
    items: getRangeSelectorItems()
  })

const createBestAnswerFilter = (): FilterSelector =>
  RadioFilterSelector.create({
    id: 'bestAnswer',
    name: 'Best answer',
    items: getBooleanSelectorItems()
  })

const createPinnedReplyFilter = (): FilterSelector =>
  RadioFilterSelector.create({
    id: 'pinnedReply',
    name: 'Pinned reply',
    items: getBooleanSelectorItems()
  })

const createUnreadByTeamFilter = (): FilterSelector =>
  RadioFilterSelector.create({
    id: 'unreadByTeam',
    name: 'Unread by team',
    items: [
      {
        id: 1,
        name: 'Unread topic',
        value: 'unreadTopic'
      },
      {
        id: 2,
        name: 'Unread reply',
        value: 'unreadReply'
      },
      {
        id: 3,
        name: 'Unread topic or reply',
        value: 'unreadTopicOrReply'
      },
      {
        id: 4,
        name: 'Read by team',
        value: 'read'
      }
    ]
  })

const createProductAreaFilter = (isMain: boolean = false): FilterSelector =>
  DynamicFilterSelector.create({
    id: 'productArea',
    name: 'Product areas',
    items: [{ id: '', name: '(No product area)', value: '' }],
    fetchUrl: '/settings/product-areas/api?q=',
    isMain
  })

const createStatusFilter = (isMain: boolean = false): FilterSelector =>
  SimpleFilterSelector.create({
    id: 'status',
    name: 'Status',
    items: [STATUS_PUBLISHED, STATUS_SCHEDULED, STATUS_DRAFT].map((status, idx) => ({
      id: idx,
      name: capitalize(status),
      value: status
    })),
    isMain
  })

export {
  createPublishedInFilter,
  createContentTypeFilter,
  createAssignedModeratorFilter,
  createPublicTagFilter,
  createAuthorFilter,
  createModerationLabelFilter,
  createModeratorTagFilter,
  createPublishedAtFilter,
  createLastActivityAtFilter,
  createChangedIdeationStatusAtFilter,
  createIdeationStatusFilter,
  createRepliesFilter,
  createLikesFilter,
  createVotesFilter,
  createBestAnswerFilter,
  createPinnedReplyFilter,
  createUnreadByTeamFilter,
  createProductAreaFilter,
  createStatusFilter
}
